import styled from '@emotion/styled';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Button, Tag, Typography, message } from 'antd';
import { FC, useEffect, useState } from 'react';
import { uploadFile } from 'services/factoryAPI';
import CustomFileUpload from './CustomFileUpload';

export interface DocumentSlot {
  document_type: string;
  label: string;
  description: string;
  required: boolean;
  initialFileId?: string;
  initialFileUrl?: string;
}

export interface BankDocumentUploadProps {
  slots: DocumentSlot[];
  onUploadComplete: (document_type: string, file_id: string, url: string) => void;
  onRemove: (document_type: string) => void;
  disabled?: boolean;
}

type SlotStatus = 'idle' | 'uploading' | 'done' | 'error';

interface PerSlotState {
  status: SlotStatus;
  fileId?: string;
  previewUrl?: string;
}

const MAX_FILE_BYTES = 5 * 1024 * 1024;

const BankDocumentUpload: FC<BankDocumentUploadProps> = ({
  slots,
  onUploadComplete,
  onRemove,
  disabled,
}) => {
  const [slotState, setSlotState] = useState<Record<string, PerSlotState>>({});

  useEffect(() => {
    const next: Record<string, PerSlotState> = {};
    for (const s of slots) {
      if (s.initialFileId) {
        next[s.document_type] = {
          status: 'done',
          fileId: s.initialFileId,
          previewUrl: s.initialFileUrl,
        };
      } else {
        next[s.document_type] = { status: 'idle' };
      }
    }
    setSlotState(next);
  }, [slots]);

  const runUploadForSlot = (document_type: string, file: File) => {
    if (file.size > MAX_FILE_BYTES) {
      message.error('ไฟล์ขนาดใหญ่เกิน 5 MB');
      return;
    }
    setSlotState((prev) => ({
      ...prev,
      [document_type]: {
        ...prev[document_type],
        status: 'uploading',
      },
    }));

    uploadFile(file)
      .then((res) => {
        setSlotState((prev) => ({
          ...prev,
          [document_type]: {
            status: 'done',
            fileId: res.id,
            previewUrl: res.url,
          },
        }));
        onUploadComplete(document_type, res.id, res.url);
      })
      .catch(() => {
        setSlotState((prev) => ({
          ...prev,
          [document_type]: {
            status: 'error',
          },
        }));
      });
  };

  const handleRemoveSlot = (slot: DocumentSlot) => {
    setSlotState((prev) => ({
      ...prev,
      [slot.document_type]: { status: 'idle' },
    }));
    onRemove(slot.document_type);
  };

  if (slots.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      <SectionTitle level={5}>เอกสารประกอบ</SectionTitle>
      <SlotsList>
      {slots.map((slot) => {
        const st = slotState[slot.document_type] ?? { status: 'idle' as const };
        const statusIcon =
          st.status === 'uploading' ? (
            <LoadingOutlined style={{ color: '#1890ff', fontSize: 16 }} />
          ) : st.status === 'done' ? (
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
          ) : st.status === 'error' ? (
            <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
          ) : (
            <UploadOutlined style={{ color: '#8c8c8c', fontSize: 16 }} />
          );

        return (
          <SlotRow key={slot.document_type}>
            <LabelBlock>
              <LabelInner>
                <IconWrap aria-hidden>{statusIcon}</IconWrap>
                <TextStack>
                  <TitleRow>
                    <LabelText>{slot.label}</LabelText>
                    {slot.required ? (
                      <CompactTag color="red">จำเป็น</CompactTag>
                    ) : (
                      <CompactTag>ไม่บังคับ</CompactTag>
                    )}
                  </TitleRow>
                  {st.status === 'done' && (
                    <CompactTag color="success">อัพโหลดแล้ว</CompactTag>
                  )}
                  {st.status === 'uploading' && (
                    <CompactTag color="processing">กำลังอัพโหลด…</CompactTag>
                  )}
                  {st.status === 'error' && (
                    <Typography.Text type="danger" className="slot-error-msg">
                      อัพโหลดไม่สำเร็จ กรุณาลองใหม่
                    </Typography.Text>
                  )}
                </TextStack>
              </LabelInner>
            </LabelBlock>
            <ActionsBlock className="slot-doc-actions">
              <CustomFileUpload
                disabled={disabled || st.status === 'uploading'}
                onFileSelected={(file) => runUploadForSlot(slot.document_type, file)}
              >
                {(openFileDialog) => (
                  <Button
                    type="default"
                    size="small"
                    icon={<UploadOutlined style={{ fontSize: 12 }} />}
                    disabled={disabled || st.status === 'uploading'}
                    onClick={openFileDialog}
                  >
                    {st.status === 'done' ? 'เปลี่ยนไฟล์' : 'อัพโหลด'}
                  </Button>
                )}
              </CustomFileUpload>
              {!slot.required && st.status === 'done' && (
                <Button type="link" size="small" danger disabled={disabled} onClick={() => handleRemoveSlot(slot)} style={{ padding: '0 4px', height: 24, fontSize: 12 }}>
                  ลบ
                </Button>
              )}
            </ActionsBlock>
          </SlotRow>
        );
      })}
      </SlotsList>
    </Wrapper>
  );
};

export default BankDocumentUpload;

const Wrapper = styled.div`
  width: 100%;
  background-color: #ffffff;
  padding: 14px 16px 16px;
  margin-top: 12px;
  border-radius: 8px;
`;

const SectionTitle = styled(Typography.Title)`
  && {
    margin: 0 0 8px;
    font-size: 15px;
    line-height: 1.35;
    font-weight: 600;
  }
`;

const SlotsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SlotRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px 10px;
  padding: 6px 10px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 6px;

  .slot-doc-actions .ant-btn-sm {
    height: 24px;
    padding: 0 8px;
    font-size: 12px;
  }
`;

const CompactTag = styled(Tag)`
  margin-inline-end: 0 !important;
  font-size: 12px;
  line-height: 18px;
  padding: 0 6px;
  border-radius: 4px;
`;

const LabelBlock = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 50px;
`;

const LabelInner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
`;

const IconWrap = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  margin-top: 3px;
  line-height: 1;
`;

const TextStack = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;

  .slot-error-msg {
    display: block;
    margin: 0;
    line-height: 1.35;
    font-size: 13px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 8px;
  width: 100%;
`;

const LabelText = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 15px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.85);
`;

const ActionsBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;
